import { useQuery } from '@apollo/client';
import { Box, Button, Chip, Typography, type DialogProps } from '@mui/material';
import type {
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { DataGridPagination, ScrollableDataGrid, hiddenHeader } from '~/components/data-grid';
import { DeviceStatus } from '~/components/devices';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { SearchBar } from '~/components/forms/search-bar';
import { AuthorizedLink } from '~/components/links';
import { ListCheckbox } from '~/components/list-row';
import { useAppContext } from '~/contexts';
import type { DevicesOrderBy } from '~/generated/graphql';
import { useLocalOrderBy } from '~/hooks/order-by';
import { useTotalCount } from '~/hooks/pagination';
import type { RemoteTargetDevice } from './AddRemoteTargets';
import { AddRemoteTargetsDocument } from './AddRemoteTargets.generated';

export interface AddRemoteTargetsDialogProps extends Omit<DialogProps, 'onChange'> {
  currentDeviceId: number;
  onChange: (devices: RemoteTargetDevice[]) => void;
  remoteTargets: readonly RemoteTargetDevice[];
}

export const AddRemoteTargetsDialog = ({
  currentDeviceId,
  onChange,
  remoteTargets,
  ...props
}: AddRemoteTargetsDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [search, setSearch] = useState('');

  const [selectedDevices, setSelectedDevices] = useState<RemoteTargetDevice[]>([]);

  const { orderBy, sortModel, setSortModel } = useLocalOrderBy<DevicesOrderBy>([
    { field: 'name', sort: 'asc' },
  ]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const page = paginationModel.page + 1;
  const perPage = paginationModel.pageSize;

  const condition = useMemo(
    () => ({ networkId: currentNetwork.id, search }),
    [currentNetwork.id, search],
  );

  const { data, loading } = useQuery(AddRemoteTargetsDocument, {
    variables: {
      condition,
      networkId: currentNetwork.id,
      orderBy,
      page,
      perPage,
    },
  });

  const totalCount = useTotalCount(data?.network?.devices.pageInfo.nodesCount);

  const devices = useMemo(
    () => data?.network?.devices.nodes.filter((x) => x.id !== currentDeviceId) ?? [],
    [currentDeviceId, data],
  );

  const close = () => props.onClose?.({}, 'backdropClick');

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
    },
    [setPaginationModel],
  );

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
    },
    [setSortModel],
  );

  const handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      const selectedOnPage = devices.filter((d) => newSelection.includes(d.id));

      const offPageSelected = selectedDevices.filter(
        (d) => !devices.some((pageDevice) => pageDevice.id === d.id),
      );

      setSelectedDevices([...offPageSelected, ...selectedOnPage]);
    },
    [devices, selectedDevices],
  );

  useEffect(() => {
    setSelectedDevices([...remoteTargets]);
  }, [remoteTargets]);

  const selectedIds = useMemo(() => selectedDevices.map((d) => d.id), [selectedDevices]);

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.devices.show.remote_targets.title')}</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
          paddingBottom: 0,
        }}
      >
        <DialogContentTitle>
          {t('pages.devices.show.remote_targets.description')}
        </DialogContentTitle>

        <ListPageContainer sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <ListContainer
            sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <ListHeader>
              <ListActions>
                <Box sx={{ flex: 1 }}>
                  <SearchBar
                    onChange={setSearch}
                    placeholder={t('pages.devices.show.remote_targets.search_placeholder')}
                    search={search}
                  />
                </Box>
              </ListActions>
              {selectedDevices.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('pages.devices.show.remote_targets.selected_devices', {
                      count: selectedDevices.length,
                    })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 2,
                      maxHeight: '72px', // Approx. 2 rows of chips (adjust as needed based on chip height + margin)
                      overflowY: 'auto',
                    }}
                  >
                    {selectedDevices
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((device) => (
                        <Chip
                          key={device.id}
                          label={device.name}
                          onDelete={() => {
                            setSelectedDevices((prev) => prev.filter((d) => d.id !== device.id));
                          }}
                          sx={{ justifyContent: 'space-between', px: 1, py: 1.75 }}
                        />
                      ))}
                  </Box>
                </Box>
              )}
            </ListHeader>

            <ListContent sx={{ overflow: 'hidden', flex: 1, minHeight: 0 }}>
              <Box sx={{ height: '100%', minHeight: 300 }}>
                <ScrollableDataGrid
                  checkboxSelection
                  columns={[
                    {
                      field: 'status',
                      ...hiddenHeader(t('common.labels.status')),
                      sortable: false,
                      resizable: false,
                      width: 40,
                      renderCell: ({ row }) => <DeviceStatus device={row} />,
                    },
                    { field: 'id', width: 65 },
                    { field: 'name', flex: 1 },
                    {
                      field: 'group',
                      flex: 1,
                      headerName: t('common.labels.group'),
                      minWidth: 100,
                      resizable: true,
                      sortable: false,
                      renderCell: ({ row }) => (
                        <AuthorizedLink
                          authorized={currentNetwork.canManage.value}
                          entity={row.group}
                        />
                      ),
                    },
                    {
                      field: 'ipAddress',
                      width: 150,
                      headerName: t('common.labels.ip_address'),
                    },
                  ]}
                  // TODO: use new shared pagination footer
                  hideFooter={false}
                  loading={loading}
                  onPaginationModelChange={onPaginationModelChange}
                  onRowSelectionModelChange={handleSelectionChange}
                  pagination
                  paginationMode="server"
                  paginationModel={paginationModel}
                  rowCount={totalCount}
                  rowSelectionModel={selectedIds}
                  rows={devices}
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={onSortModelChange}
                  sx={{
                    height: '100%',
                    '& .MuiDataGrid-virtualScroller': {
                      minHeight: 300,
                    },
                  }}
                  slots={{
                    baseCheckbox: ListCheckbox,
                    pagination: DataGridPagination,
                    columnResizeIcon: () => null,
                    noRowsOverlay: () => (
                      <Box height="100%" display="flex" justifyContent="center" alignItems="center">
                        {t('pages.devices.show.remote_targets.no_devices')}
                      </Box>
                    ),
                  }}
                />
              </Box>
            </ListContent>
          </ListContainer>
        </ListPageContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>

        <Button
          color="primary"
          onClick={() => {
            onChange(selectedDevices);
            close();
          }}
          variant="contained"
        >
          {t('common.dialogs.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
